import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown, FiAlertCircle, FiCheck } from 'react-icons/fi';
import styles from '../Auth.module.css';

/**
 * CategorizedRoleSelect - Searchable dropdown with roles grouped by category
 * Uses React Portal to render dropdown outside of parent DOM hierarchy
 */
const CategorizedRoleSelect = ({
  rolesData,
  selectedRole,
  selectedCategory,
  onRoleSelect,
  isCustomRole,
  onCustomRoleToggle,
  customRoleValue,
  onCustomRoleChange,
  error,
  disabled = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  
  const triggerRef = useRef(null);

  // Calculate dropdown position based on trigger element
  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 300;
    const spaceBelow = viewportHeight - rect.bottom - 16;
    
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(dropdownMaxHeight, Math.max(200, spaceBelow)),
      zIndex: 99999,
    });
  }, []);

  // Update position when opening
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      
      const handleScrollResize = () => updateDropdownPosition();
      window.addEventListener('scroll', handleScrollResize, true);
      window.addEventListener('resize', handleScrollResize);
      
      return () => {
        window.removeEventListener('scroll', handleScrollResize, true);
        window.removeEventListener('resize', handleScrollResize);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Filter roles based on search query
  const filteredCategories = useMemo(() => {
    if (!rolesData?.categories) return {};
    
    const query = searchQuery.toLowerCase().trim();
    if (!query) return rolesData.categories;

    const filtered = {};
    Object.entries(rolesData.categories).forEach(([key, categoryData]) => {
      const matchingRoles = categoryData.roles.filter(role =>
        role.toLowerCase().includes(query)
      );
      if (matchingRoles.length > 0) {
        filtered[key] = {
          ...categoryData,
          roles: matchingRoles,
        };
      }
    });
    return filtered;
  }, [rolesData, searchQuery]);

  const handleRoleClick = (role, categoryKey) => {
    onRoleSelect(role, categoryKey);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  // Dropdown content to be portaled
  const dropdownContent = isOpen ? createPortal(
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99998,
        }}
        onClick={handleClose}
      />
      
      {/* Dropdown */}
      <div 
        style={{
          ...dropdownStyle,
          background: 'white',
          border: '2px solid #e5e7eb',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Search input */}
        <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search roles..."
            autoFocus
            style={{
              width: '100%',
              padding: '10px 14px',
              fontSize: '14px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              outline: 'none',
              textAlign: 'center',
            }}
            onFocus={(e) => e.target.style.borderColor = '#10b981'}
            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
          />
        </div>

        {/* Options list */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          maxHeight: dropdownStyle.maxHeight ? dropdownStyle.maxHeight - 68 : 232,
        }}>
          {Object.entries(filteredCategories).length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
              No roles found matching "{searchQuery}"
            </div>
          ) : (
            Object.entries(filteredCategories).map(([categoryKey, categoryData]) => (
              <div key={categoryKey}>
                <div style={{
                  padding: '8px 16px',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: '#10b981',
                  background: 'rgba(16, 185, 129, 0.08)',
                }}>
                  {categoryData.category}
                </div>
                {categoryData.roles.map((role) => (
                  <button
                    type="button"
                    key={role}
                    onClick={() => handleRoleClick(role, categoryKey)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      padding: '10px 16px',
                      background: selectedRole === role ? 'rgba(16, 185, 129, 0.12)' : 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      fontSize: '14px',
                      color: selectedRole === role ? '#10b981' : '#374151',
                      fontWeight: selectedRole === role ? 500 : 400,
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedRole !== role) e.target.style.background = 'rgba(16, 185, 129, 0.06)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = selectedRole === role ? 'rgba(16, 185, 129, 0.12)' : 'transparent';
                    }}
                  >
                    <span>{role}</span>
                    {selectedRole === role && <FiCheck size={16} />}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </>,
    document.body
  ) : null;

  if (isCustomRole) {
    return (
      <div className={styles['au-role-select']}>
        <div className={styles['au-input-group']}>
          <label className={styles['au-input-group__label']}>Your Role</label>
          <input
            type="text"
            value={customRoleValue || ''}
            onChange={(e) => onCustomRoleChange(e.target.value)}
            placeholder="Enter your professional role"
            className={styles['au-input']}
            disabled={disabled}
          />
        </div>
        
        <label className={styles['au-checkbox']} style={{ marginTop: '0.75rem' }}>
          <input
            type="checkbox"
            className={styles['au-checkbox__input']}
            checked={isCustomRole}
            onChange={(e) => onCustomRoleToggle(e.target.checked)}
            disabled={disabled}
          />
          <span className={styles['au-checkbox__label']}>
            My role is not listed
          </span>
        </label>
      </div>
    );
  }

  return (
    <div className={styles['au-role-select']}>
      <div className={styles['au-input-group']}>
        <label className={styles['au-input-group__label']}>Professional Role</label>
        
        <div className={styles['au-role-select__container']}>
          {/* Trigger button */}
          <button
            ref={triggerRef}
            type="button"
            className={`${styles['au-select']} ${styles['au-role-select__trigger']}`}
            onClick={handleOpen}
            disabled={disabled}
          >
            <span className={selectedRole ? '' : styles['au-role-select__placeholder']}>
              {selectedRole || 'Select your role'}
            </span>
            <FiChevronDown 
              className={styles['au-select-wrapper__icon']} 
              style={{ 
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}
            />
          </button>

          {/* Portal-rendered dropdown */}
          {dropdownContent}
        </div>
      </div>

      {/* Custom role toggle */}
      <label className={styles['au-checkbox']} style={{ marginTop: '0.75rem' }}>
        <input
          type="checkbox"
          className={styles['au-checkbox__input']}
          checked={isCustomRole}
          onChange={(e) => onCustomRoleToggle(e.target.checked)}
          disabled={disabled}
        />
        <span className={styles['au-checkbox__label']}>
          My role is not listed
        </span>
      </label>

      {/* Error display */}
      {error && (
        <div className={styles['au-input-group__error']}>
          <FiAlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default CategorizedRoleSelect;



